import dayjs from "dayjs";

import { currenciesList, DEFAULT_CURRENCY } from "app/common/constants/currencies";
import { GraphWidget } from "app/components/widget/widget-types/GraphWidget";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberWithPrefixFilter, decimalNumberFilter } from "app/filters/ngFilters";
import { fetchSearchAdsSpend } from "app/services/searchAdsSpend/fetchSearchAdsSpend";
import { mapSearchAdsSpend } from "app/services/searchAdsSpend/searchAdsSpendMapper";
import { splitDomains } from "app/services/searchAdsSpend/searchAdsSpendParams";
import type { IAdSpendWidgetData } from "app/services/searchAdsSpend/toWidgetSeries";
import { toWidgetSeries } from "app/services/searchAdsSpend/toWidgetSeries";
import { fetchWebsiteImageQuery } from "app/services/sitesService/queries/sitesQueries";
import type { IWebsiteImageResponse } from "app/services/sitesService/types";

import { AdSpendLineChartWidgetFilters } from "../widget-filters/AdSpendLineChartWidgetFilters";

const getCurrencyRateByDate = (date, data: IAdSpendWidgetData, selectedCurrency) => {
    if (!selectedCurrency || selectedCurrency.name === DEFAULT_CURRENCY) {
        return null;
    }
    return data.rates[dayjs.utc(date).format("YYYY-MM-DD")] ?? null;
};
const AdSpendGraphDashboardTooltip = ({ points, x, selectedCurrency, currencyRateTitle }) => {
    const from = dayjs.utc(x);
    const date = from.format("MMMM YYYY");
    const lines = [];
    lines.push(
        '<div class="date">' +
            date +
            "<small>" +
            currencyRateTitle +
            "</small>" +
            "</div>" +
            '<div class="line-seperator"></div>',
    );
    points?.forEach((point) => {
        const seriesName =
            point.series.name.indexOf("$") != -1
                ? point.series.name.replace("*", "").replace("$", "").replace(/_/g, " ").replace("~", " > ")
                : point.series.name;

        lines.push(
            "<div>" +
                '<span class="item-marker" style="background: ' +
                point.series.color +
                '"></span>' +
                '<span class="item-name">' +
                seriesName +
                '<span class="item-value" style="margin-left:4px;color: ' +
                point.series.color +
                ';">' +
                `${abbrNumberWithPrefixFilter()(point.point.y, selectedCurrency.icon)}` +
                "</span></span></div>",
        );
    });
    return lines.join("");
};

export class AdSpendLineChartWidget extends GraphWidget {
    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "AdSpendLineChart";
    }

    public isPptSupported = () => {
        return false;
    };

    private dataFetcherFactory = () => {
        return {
            create: (widget) => {
                return {
                    fetch: async () => {
                        const apiParams = { ...widget.apiParams };
                        const currency = apiParams.currency || DEFAULT_CURRENCY;
                        const webSource = apiParams.webSource;

                        const response = await fetchSearchAdsSpend({
                            country: Number(apiParams.country),
                            from: apiParams.from,
                            to: apiParams.to,
                            keys: splitDomains(apiParams.keys),
                            isWindow: apiParams.isWindow,
                            webSource,
                        });

                        const spend = mapSearchAdsSpend(response, { currency, webSource });

                        return {
                            Data: toWidgetSeries(spend, {
                                webSource,
                                currency,
                                exchangeRates: response.exchangeRates,
                            }),
                        };
                    },
                };
            },
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    getHighChartsConfig(chartOptions, formattedData, data: IAdSpendWidgetData) {
        const currencyName = this.apiParams.currency || DEFAULT_CURRENCY;
        const selectedCurrency = currenciesList.find((elt) => elt.name === currencyName);
        chartOptions.format = "abbrNumberWithPrefix";
        chartOptions.formatParameter = undefined;
        chartOptions.yPercentPrecision = selectedCurrency.icon;
        const formatter = function () {
            let currencyRateTitle = "";
            const currencyRate = getCurrencyRateByDate(this.x, data, selectedCurrency);
            if (currencyRate !== null) {
                currencyRateTitle = `($1 = ${selectedCurrency.icon}${decimalNumberFilter()(currencyRate, true)})`;
            }
            return AdSpendGraphDashboardTooltip({
                points: this.points,
                x: this.x,
                selectedCurrency,
                currencyRateTitle,
            });
        };
        chartOptions.tooltip = { formatter };
        const config = super.getHighChartsConfig(chartOptions, formattedData, data);
        config.series.forEach((series) => {
            series.marker = {
                symbol: "circle",
                lineWidth: 1,
                color: series.color,
            };
        });
        return config;
    }

    protected getLegendResources(siteList: string[]) {
        let index = 0;
        return siteList.map(async (rootDomain) => {
            const item: { icon?: IWebsiteImageResponse["image"] } = {};
            await fetchWebsiteImageQuery({ website: rootDomain }).then((data) => {
                item.icon = data.image;
            });
            return {
                ...item,
                id: rootDomain,
                name: rootDomain,
                color: CHART_COLORS.chartMainColors[index],
                smallIcon: true,
                legendClass: "legend-smallHeight",
                type: "Website",
                index: index++,
            };
        });
    }

    protected validateData(data: IAdSpendWidgetData) {
        return Object.values(data?.series ?? {}).some((webSourceSeries) =>
            Object.values(webSourceSeries).some((points) => points.some((point) => point.Value !== null)),
        );
    }

    protected async getChartSeries(
        unorderedData: IAdSpendWidgetData,
        compareItemsKeys: string[] = null,
    ): Promise<any[]> {
        return await super.getChartSeries(unorderedData.series, compareItemsKeys);
    }

    static getFiltersComponent() {
        return AdSpendLineChartWidgetFilters;
    }

    _getProUrlParams() {
        const { currency } = this.apiParams;
        return Object.assign(super._getProUrlParams(), {
            currency: currency,
        });
    }

    getViewData() {
        super.getViewData();
        const titleFromMetricConfig = translate("dashboard.metrics.titles.ad.spend");
        if (this.viewData.title.includes(titleFromMetricConfig)) {
            this.viewData.title = `${this.apiParams.webSource} ${titleFromMetricConfig}`;
        }
        return this.viewData;
    }
}

AdSpendLineChartWidget.register();
